import React from 'react';
import {
  Box, Chip, Dialog, DialogContent, DialogTitle, IconButton,
  Stack, Table, TableBody, TableCell, TableRow, Typography, alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StatusChip from './StatusChip';
import { CASH_TOL, TOLERANCE_QTY, TOLERANCE_VAL } from '../../utils/derivativeReconConstants';
import { fmt, fmtN } from '../../utils/derivativeReconFormat';

function diffColor(v, tol) {
  if (v == null) return 'text.secondary';
  return Math.abs(v) > tol ? '#f87171' : '#34d399';
}

function dateMatchColor(m) {
  if (m === false) return '#f87171';
  if (m === true) return '#34d399';
  return 'text.primary';
}

export default function TradeDetailDialog({ trade, type, wires = [], onClose }) {
  if (!trade) return null;
  const isPos = type === 'Positions';
  const rows = isPos
    ? [
        ['Trade ID', trade.id],
        ['CW Raw ID', trade.rawCwId || '—'],
        ['Entity', trade.entity || '—'],
        ['Broker', trade.cpName],
        ['Mil Notional', fmt(trade.milVal)],
        ['CW Notional', fmt(trade.cwVal)],
        ['Value Diff', <Typography component="span" key="vd" sx={{ color: diffColor(trade.valDiff, TOLERANCE_VAL), fontWeight: 700 }}>{fmt(trade.valDiff)}</Typography>],
        ['Mil # Options', fmtN(trade.milQty)],
        ['CW Units', fmtN(trade.cwQty)],
        ['Qty Diff', <Typography component="span" key="qd" sx={{ color: Math.abs(trade.qtyDiff || 0) < TOLERANCE_QTY ? '#34d399' : '#f87171', fontWeight: 700 }}>{fmtN(trade.qtyDiff)}</Typography>],
        ['Mil Strike', fmt(trade.milStrike)],
        ['CW Strike', fmt(trade.cwStrike)],
        ['Strike Diff', <Typography component="span" key="sd" sx={{ color: diffColor(trade.strikeDiff, TOLERANCE_VAL), fontWeight: 700 }}>{fmt(trade.strikeDiff)}</Typography>],
        ['Mil Trade Date', trade.milTrade || '—'],
        ['CW Trade Date', <Typography component="span" key="ctd" sx={{ color: dateMatchColor(trade.tradeDtMatch), fontWeight: 600 }}>{trade.cwTrade || (trade.tradeDtMatch === null ? '(before cutoff)' : '—')}</Typography>],
        ['Mil Settlement Date', trade.milSettle || '—'],
        ['CW Settle Date', <Typography component="span" key="csd" sx={{ color: dateMatchColor(trade.settleDtMatch), fontWeight: 600 }}>{trade.cwSettle || (trade.settleDtMatch === null ? '(before cutoff)' : '—')}</Typography>],
        ['Status', <StatusChip key="st" status={trade.status} />],
      ]
    : [
        ['Trade ID', trade.id],
        ['Entity', trade.entity || '—'],
        ['Broker', trade.cpName],
        ['Settlement Date', trade.settle || '—'],
        [`Milliman ${type} Total`, fmt(trade.milTotal)],
        [`CW ${type} Total`, fmt(trade.cwTotal)],
        ['Difference', <Typography component="span" key="d" sx={{ color: Math.abs(trade.diff || 0) > TOLERANCE_VAL ? '#f87171' : '#34d399', fontWeight: 700 }}>{fmt(trade.diff)}</Typography>],
        ['Status', <StatusChip key="st" status={trade.status} />],
      ];

  return (
    <Dialog open={!!trade} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={800}>Trade Detail — {trade.id}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={row[0]} sx={{ bgcolor: i % 2 ? 'action.hover' : 'transparent' }}>
                  <TableCell sx={{ width: 200, color: 'text.secondary', fontWeight: 600, fontSize: 12 }}>{row[0]}</TableCell>
                  <TableCell sx={{ color: 'text.primary', fontSize: 12 }}>{row[1]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {!isPos && wires.map((w) => (
          <WirePanel key={w.wireId || JSON.stringify(w)} wire={w} currentTradeId={trade.id} />
        ))}
        {!isPos && !wires.length && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: 'divider', color: 'text.secondary', fontSize: 12 }}>
            No matching custody wire found for this trade. Run Cash Rec with the
            relevant custody file to link this break to a wire.
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, alt }) {
  return (
    <TableRow sx={{ bgcolor: alt ? 'action.hover' : 'transparent' }}>
      <TableCell sx={{ width: 200, color: 'text.secondary', fontWeight: 600, fontSize: 12 }}>{label}</TableCell>
      <TableCell sx={{ color: 'text.primary', fontSize: 12 }}>{value}</TableCell>
    </TableRow>
  );
}

/** The custody wire this trade settled on, plus everything else it rode with. */
function WirePanel({ wire, currentTradeId }) {
  const wireDiff = wire?.diff;
  const diffOk = wireDiff != null && Math.abs(wireDiff) <= CASH_TOL;
  const accent = diffOk ? '#34d399' : '#fbbf24';
  const members = wire.members || [];
  const otherTradeIds = (wire.tradeIds || []).filter((id) => String(id) !== String(currentTradeId));

  return (
    <Box sx={{ mt: 2, borderRadius: 1, border: '1px solid', borderColor: alpha(accent, 0.5), bgcolor: alpha(accent, 0.06), overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <AccountBalanceIcon sx={{ color: accent, fontSize: 18 }} />
        <Typography fontWeight={800} fontSize={13}>Custody Wire {wire.wireId || ''}</Typography>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label={wire.source || '—'} sx={{ fontSize: 10, fontWeight: 700, bgcolor: alpha('#5b7fff', 0.18), color: '#5b7fff' }} />
      </Stack>
      <Table size="small">
        <TableBody>
          <DetailRow label="Wire Date" value={wire.dateStr || '—'} />
          <DetailRow label="Wire Amount (custody)" value={wire.amt != null ? fmt(Math.abs(wire.amt)) : '—'} alt />
          <DetailRow label="Wire Account" value={wire.acctNum || '—'} />
          <DetailRow label="Sum of Trades on Wire" value={wire.total != null ? fmt(wire.total) : '—'} alt />
          <DetailRow
            label="Diff"
            value={wireDiff != null
              ? <Typography component="span" sx={{ color: diffOk ? '#34d399' : '#f87171', fontWeight: 700 }}>{fmt(wireDiff)}</Typography>
              : '—'}
          />
          <DetailRow label="Description" value={wire.desc || '—'} alt />
        </TableBody>
      </Table>
      <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography fontWeight={700} fontSize={11} color="text.secondary" mb={0.75}>
          {members.length} trade{members.length === 1 ? '' : 's'} bundled in this wire
        </Typography>
        <Stack spacing={0.5}>
          {members.map((m, i) => (
            <Stack key={`${m.entity}-${m.broker}-${i}`} direction="row" alignItems="center" spacing={1} sx={{ fontSize: 12 }}>
              <Chip
                size="small"
                label={m.type}
                sx={{
                  fontSize: 10, fontWeight: 700, height: 18,
                  bgcolor: m.type === 'Premium' ? alpha('#38bdf8', 0.18) : alpha('#fbbf24', 0.18),
                  color: m.type === 'Premium' ? '#38bdf8' : '#fbbf24',
                }}
              />
              <Box sx={{ minWidth: 60, color: 'text.secondary', fontWeight: 600 }}>{m.entity || '—'}</Box>
              <Box sx={{ flex: 1, color: 'text.primary' }}>{m.broker || '—'}</Box>
              <Box sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fmt(m.total)}</Box>
            </Stack>
          ))}
        </Stack>
        {otherTradeIds.length > 0 && (
          <Box sx={{ mt: 1.25 }}>
            <Typography fontWeight={700} fontSize={11} color="text.secondary" mb={0.5}>
              Other trade IDs on this wire
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {otherTradeIds.map((id) => (
                <Chip key={id} size="small" label={id} sx={{ fontFamily: 'monospace', fontSize: 11 }} />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}
